class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-link');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.dispatchCartViewEvent();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  dispatchCartViewEvent() {
    customElements.whenDefined('s-view-event').then(() => {
      const viewEvent = this.querySelector('s-view-event[view-event-trigger="manual"]');
      if (viewEvent && typeof viewEvent.dispatchViewEvent === 'function') {
        viewEvent.dispatchViewEvent();
      }
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, allowSectionRefresh = true) {
    const sectionsToRender = this.getSectionsToRender();
    const replacements = this.getSectionReplacements(parsedState, sectionsToRender);

    if (!replacements) {
      if (allowSectionRefresh) {
        this.refreshSections(parsedState, sectionsToRender);
      } else {
        this.openCartPage();
      }
      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (!drawerInner) {
      this.openCartPage();
      return;
    }

    if (drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    replacements.forEach(({ sectionElement, sectionContent }) => {
      sectionElement.innerHTML = sectionContent.innerHTML;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionReplacements(parsedState, sectionsToRender) {
    if (!parsedState || !parsedState.sections) return null;

    const replacements = sectionsToRender.map((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections[section.id];
      const sectionContent = typeof sectionHTML === 'string'
        ? this.getSectionDOM(sectionHTML, section.selector)
        : null;

      return { sectionElement, sectionContent };
    });

    return replacements.every(({ sectionElement, sectionContent }) => sectionElement && sectionContent)
      ? replacements
      : null;
  }

  refreshSections(parsedState, sectionsToRender) {
    if (this.sectionRefreshPromise) return;

    const cartUrl = window.routes?.cart_url || '/cart';
    const requestUrl = new URL(cartUrl, window.location.origin);
    requestUrl.searchParams.set('sections', sectionsToRender.map((section) => section.id).join(','));

    this.sectionRefreshPromise = fetch(requestUrl.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart section refresh failed with status ${response.status}`);
        return response.json();
      })
      .then((sections) => {
        this.renderContents({ ...parsedState, sections }, false);
      })
      .catch((error) => {
        console.error('Unable to refresh cart sections after add to cart.', error);
        this.openCartPage();
      })
      .finally(() => {
        this.sectionRefreshPromise = null;
      });
  }

  openCartPage() {
    window.location.assign(window.routes?.cart_url || '/cart');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
